import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  useCallback,
} from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Snackbar,
  Alert,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import CameraswitchIcon from "@mui/icons-material/Cameraswitch";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import dayjs from "dayjs";
import jsQR from "jsqr";
import { socket } from "../../socket";
import {
  getMuestraByQrCode,
  addMovimiento,
} from "../../api/Muestras/muestraApi";
import { SidebarContext } from "../Sidebar/SidebarProvider";
import EnvioDestinoFields, {
  emptyEnvioDestino,
  isEnvioDestinoValid,
  toMovimientoDestinoPayload,
  DESTINO_CLIENTE,
  DESTINO_PROVEEDOR,
  DESTINO_INTERNO,
} from "./EnvioDestinoFields";

const SCAN_INTERVAL_MS = 450;
const JSQR_MAX_WIDTH = 480;
const DETECT_COOLDOWN_MS = 2500;
const SCAN_MODE_CAMERA = "camera";
const SCAN_MODE_GUN = "gun";
const SCAN_MODE_STORAGE_KEY = "muestras-scan-mode";

const readStoredScanMode = (isMobile) => {
  try {
    const stored = localStorage.getItem(SCAN_MODE_STORAGE_KEY);
    if (stored === SCAN_MODE_CAMERA || stored === SCAN_MODE_GUN) return stored;
  } catch {
    /* ignore */
  }
  return isMobile ? SCAN_MODE_CAMERA : SCAN_MODE_GUN;
};

const MuestrasMovimientoCamara = ({
  embedded = false,
  title = "SAMPLE SCANNER",
  confirmLabel = null,
  initialDestino = null,
  allowedTipos = [DESTINO_CLIENTE, DESTINO_PROVEEDOR, DESTINO_INTERNO],
  /** Si se pasa, sustituye el registro por defecto (addMovimiento). */
  customRegister = null,
  onRegistered = null,
  hideCourier = false,
  /** Preferir pistola / código a mano (útil en reuniones). */
  preferGunMode = false,
  /** Modo reutilizable: seleccionar muestras sin registrar un movimiento. */
  selectionOnly = false,
  selectionListTitle = "Selección",
  selectionFooter = null,
  confirmSelection = true,
} = {}) => {
  const { contentMargin, isMobile } = useContext(SidebarContext);

  const [scanMode, setScanMode] = useState(() =>
    preferGunMode ? SCAN_MODE_GUN : readStoredScanMode(isMobile),
  );
  const [gunBuffer, setGunBuffer] = useState("");
  const [lastQrCode, setLastQrCode] = useState("");
  const [scannedItems, setScannedItems] = useState([]);
  const [destino, setDestino] = useState(() =>
    initialDestino
      ? { ...emptyEnvioDestino(), ...initialDestino }
      : emptyEnvioDestino(),
  );
  const [numCourier, setNumCourier] = useState("");
  const [mensaje, setMensaje] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("success");
  const [open, setOpen] = useState(false);
  const [hasPermission, setHasPermission] = useState(null);
  const [devices, setDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState(null);
  const [preferredFacingMode, setPreferredFacingMode] = useState("environment");
  const [loading, setLoading] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [registerProgress, setRegisterProgress] = useState({
    done: 0,
    total: 0,
  });
  const [hasMultipleCameras, setHasMultipleCameras] = useState(false);
  const [confirmSelectionOpen, setConfirmSelectionOpen] = useState(false);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const gunInputRef = useRef(null);
  const beepRef = useRef(null);
  const detectionTimer = useRef(null);
  const streamRef = useRef(null);
  const detectorRef = useRef(null);
  const scanInProgressRef = useRef(false);
  const lastDetectedCodeRef = useRef("");
  const lastDetectedAtRef = useRef(0);
  const loadingRef = useRef(false);
  const registeringRef = useRef(false);
  const scannedIdsRef = useRef(new Set());
  const jsqrTickRef = useRef(0);
  const cancelledRef = useRef(false);
  const scanModeRef = useRef(scanMode);
  const customRegisterRef = useRef(customRegister);
  const onRegisteredRef = useRef(onRegistered);

  useEffect(() => {
    customRegisterRef.current = customRegister;
  }, [customRegister]);

  useEffect(() => {
    onRegisteredRef.current = onRegistered;
  }, [onRegistered]);

  useEffect(() => {
    if (!initialDestino) return;
    setDestino((prev) => ({ ...prev, ...initialDestino }));
  }, [initialDestino]);

  useEffect(() => {
    beepRef.current = new Audio(
      "https://actions.google.com/sounds/v1/cartoon/wood_plank_flicks.ogg",
    );
    beepRef.current.volume = 0.4;
  }, []);

  const showFeedback = useCallback((text, severity = "success") => {
    setMensaje(text);
    setAlertSeverity(severity);
    setOpen(true);
  }, []);

  const feedback = useCallback(() => {
    try {
      if (beepRef.current) beepRef.current.play().catch(() => {});
      if (navigator.vibrate) navigator.vibrate(120);
    } catch {}
  }, []);

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  useEffect(() => {
    registeringRef.current = registering;
  }, [registering]);

  useEffect(() => {
    scannedIdsRef.current = new Set(
      scannedItems.map((item) => item.ID_MUESTRA),
    );
  }, [scannedItems]);

  const stopCurrentStream = useCallback(() => {
    if (detectionTimer.current) {
      clearTimeout(detectionTimer.current);
      detectionTimer.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  useEffect(() => {
    scanModeRef.current = scanMode;
    try {
      localStorage.setItem(SCAN_MODE_STORAGE_KEY, scanMode);
    } catch {
      /* ignore */
    }
    if (scanMode === SCAN_MODE_GUN) {
      stopCurrentStream();
      setHasPermission(null);
      setSelectedDeviceId(null);
    }
  }, [scanMode, stopCurrentStream]);

  const focusGunInput = useCallback(() => {
    if (scanModeRef.current !== SCAN_MODE_GUN) return;
    const active = document.activeElement;
    if (
      active &&
      active !== document.body &&
      active !== gunInputRef.current &&
      (active.tagName === "INPUT" ||
        active.tagName === "TEXTAREA" ||
        active.tagName === "SELECT" ||
        active.getAttribute("role") === "combobox" ||
        active.isContentEditable)
    ) {
      return;
    }
    gunInputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (scanMode !== SCAN_MODE_GUN) return undefined;
    const t = setTimeout(focusGunInput, 50);
    return () => clearTimeout(t);
  }, [scanMode, loading, registering, focusGunInput]);

  const isBackCameraLabel = useCallback(
    (label = "") =>
      /(back|rear|environment|trase|trasera|posterior)/i.test(label),
    [],
  );

  const getPreferredBackCamera = useCallback(
    (videoInputs = []) =>
      videoInputs.find((d) => isBackCameraLabel(d.label)) || videoInputs[0],
    [isBackCameraLabel],
  );

  const waitForVideoReady = (videoEl, timeoutMs = 4000) =>
    new Promise((resolve, reject) => {
      if (!videoEl) {
        reject(new Error("Video no disponible"));
        return;
      }

      if (videoEl.readyState >= 2 && videoEl.videoWidth > 0) {
        resolve();
        return;
      }

      let settled = false;
      const timeout = setTimeout(() => {
        if (settled) return;
        settled = true;
        cleanup();
        if (videoEl.videoWidth > 0) resolve();
        else reject(new Error("Timeout esperando frames de cámara"));
      }, timeoutMs);

      const onReady = () => {
        if (settled || videoEl.videoWidth <= 0) return;
        settled = true;
        cleanup();
        resolve();
      };

      const cleanup = () => {
        clearTimeout(timeout);
        videoEl.removeEventListener("loadedmetadata", onReady);
        videoEl.removeEventListener("canplay", onReady);
        videoEl.removeEventListener("playing", onReady);
      };

      videoEl.addEventListener("loadedmetadata", onReady);
      videoEl.addEventListener("canplay", onReady);
      videoEl.addEventListener("playing", onReady);
    });

  useEffect(() => {
    if (scanMode !== SCAN_MODE_CAMERA) return undefined;

    let cancelled = false;

    const initCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" } },
        });
        if (cancelled || scanModeRef.current !== SCAN_MODE_CAMERA) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }

        setHasPermission(true);

        if ("BarcodeDetector" in window) {
          try {
            detectorRef.current = new window.BarcodeDetector({
              formats: ["qr_code"],
            });
          } catch {
            detectorRef.current = null;
          }
        }

        const allDevices = await navigator.mediaDevices.enumerateDevices();
        const videoInputs = allDevices.filter((d) => d.kind === "videoinput");
        setDevices(videoInputs);
        setHasMultipleCameras(videoInputs.length > 1);

        const back = getPreferredBackCamera(videoInputs);
        setSelectedDeviceId(
          back?.deviceId || videoInputs[0]?.deviceId || "default",
        );

        stream.getTracks().forEach((t) => t.stop());
      } catch (err) {
        console.error("Error acceso cámara:", err);
        if (!cancelled) setHasPermission(false);
      }
    };

    initCamera();
    return () => {
      cancelled = true;
    };
  }, [scanMode, getPreferredBackCamera]);

  const handleResult = useCallback(
    async (code) => {
      if (!code || loadingRef.current || registeringRef.current) return;
      setLoading(true);
      setLastQrCode(code);
      try {
        const data = await getMuestraByQrCode(code);
        if (!data || !data.ID_MUESTRA) {
          showFeedback("❌ Muestra no encontrada", "error");
          return;
        }

        if (scannedIdsRef.current.has(data.ID_MUESTRA)) {
          showFeedback(`⚠️ ${data.MUESTRA} ya está en la lista`, "warning");
          return;
        }

        const productoNombre =
          data.PRODUCTO ||
          data.NOMBRE_DISENO ||
          data.producto?.nombre ||
          data.NombreCliente ||
          "";

        feedback();
        setScannedItems((prev) => [
          {
            ID_MUESTRA: data.ID_MUESTRA,
            MUESTRA: data.MUESTRA,
            PRODUCTO: productoNombre,
            qrCode: code,
          },
          ...prev,
        ]);
        showFeedback(`✅ ${data.MUESTRA} añadida`, "success");
      } catch (error) {
        const status = error?.response?.status;
        if (status === 404) {
          showFeedback("❌ Muestra no encontrada", "error");
        } else if (status === 400) {
          showFeedback("❌ Código no válido", "error");
        } else {
          showFeedback("❌ Error al procesar QR", "error");
        }
      } finally {
        setLoading(false);
        setGunBuffer("");
        if (scanModeRef.current === SCAN_MODE_GUN) {
          setTimeout(() => gunInputRef.current?.focus(), 30);
        }
      }
    },
    [feedback, showFeedback],
  );

  const submitGunScan = useCallback(() => {
    const code = gunBuffer.trim();
    if (!code) return;
    setGunBuffer("");
    handleResult(code);
  }, [gunBuffer, handleResult]);

  const handleGunKeyDown = useCallback(
    (event) => {
      if (event.key !== "Enter") return;
      event.preventDefault();
      submitGunScan();
    },
    [submitGunScan],
  );

  const removeItem = useCallback((idMuestra) => {
    setScannedItems((prev) =>
      prev.filter((item) => item.ID_MUESTRA !== idMuestra),
    );
  }, []);

  const clearList = useCallback(() => {
    setScannedItems([]);
    setLastQrCode("");
    lastDetectedCodeRef.current = "";
    lastDetectedAtRef.current = 0;
  }, []);

  const decodeFrame = useCallback(async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || video.readyState < 2) return null;

    const width = video.videoWidth;
    const height = video.videoHeight;
    if (!width || !height) return null;

    if (detectorRef.current) {
      try {
        const barcodes = await detectorRef.current.detect(video);
        if (barcodes.length > 0) return barcodes[0].rawValue;
      } catch {
        // fallback jsQR
      }
    }

    jsqrTickRef.current += 1;
    const everyN = detectorRef.current ? 3 : 1;
    if (jsqrTickRef.current % everyN !== 0) return null;

    const scale = Math.min(1, JSQR_MAX_WIDTH / width);
    const targetW = Math.max(1, Math.round(width * scale));
    const targetH = Math.max(1, Math.round(height * scale));

    canvas.width = targetW;
    canvas.height = targetH;
    const context = canvas.getContext("2d", { willReadFrequently: true });
    if (!context) return null;

    context.drawImage(video, 0, 0, targetW, targetH);
    const imageData = context.getImageData(0, 0, targetW, targetH);
    const qr = jsQR(imageData.data, targetW, targetH, {
      inversionAttempts: "dontInvert",
    });
    return qr?.data || null;
  }, []);

  useEffect(() => {
    if (scanMode !== SCAN_MODE_CAMERA || !selectedDeviceId || !hasPermission) {
      return undefined;
    }

    cancelledRef.current = false;

    const scheduleNext = () => {
      if (cancelledRef.current) return;
      detectionTimer.current = setTimeout(tick, SCAN_INTERVAL_MS);
    };

    const tick = async () => {
      if (
        cancelledRef.current ||
        scanInProgressRef.current ||
        loadingRef.current ||
        registeringRef.current
      ) {
        scheduleNext();
        return;
      }

      scanInProgressRef.current = true;
      try {
        const code = await decodeFrame();
        if (code && !loadingRef.current && !cancelledRef.current) {
          const now = Date.now();
          const isSameCode = code === lastDetectedCodeRef.current;
          const withinCooldown =
            now - lastDetectedAtRef.current < DETECT_COOLDOWN_MS;

          if (!isSameCode || !withinCooldown) {
            lastDetectedCodeRef.current = code;
            lastDetectedAtRef.current = now;
            handleResult(code);
          }
        }
      } catch (err) {
        console.error("Error detectando QR:", err);
      } finally {
        scanInProgressRef.current = false;
        scheduleNext();
      }
    };

    const startStream = async () => {
      try {
        stopCurrentStream();
        if (cancelledRef.current) return;

        const idealWidth = isMobile ? 640 : 1280;
        const idealHeight = isMobile ? 480 : 720;

        const attempts = [];

        if (selectedDeviceId && selectedDeviceId !== "default") {
          attempts.push({
            video: {
              deviceId: { exact: selectedDeviceId },
              width: { ideal: idealWidth },
              height: { ideal: idealHeight },
            },
          });
          attempts.push({
            video: {
              deviceId: { ideal: selectedDeviceId },
              facingMode: { ideal: preferredFacingMode },
              width: { ideal: idealWidth },
              height: { ideal: idealHeight },
            },
          });
        }

        attempts.push({
          video: {
            facingMode: { ideal: preferredFacingMode },
            width: { ideal: idealWidth },
            height: { ideal: idealHeight },
          },
        });
        attempts.push({
          video: { facingMode: preferredFacingMode },
        });
        attempts.push({ video: true });

        let stream = null;
        let streamError = null;
        for (const constraints of attempts) {
          try {
            stream = await navigator.mediaDevices.getUserMedia(constraints);
            break;
          } catch (error) {
            streamError = error;
          }
        }

        if (!stream) {
          throw (
            streamError || new Error("No se pudo crear el stream de cámara")
          );
        }

        if (cancelledRef.current) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }

        streamRef.current = stream;
        const video = videoRef.current;
        if (video) {
          video.setAttribute("playsinline", "true");
          video.setAttribute("webkit-playsinline", "true");
          video.muted = true;
          video.srcObject = stream;

          try {
            await video.play();
          } catch {
            await new Promise((r) => setTimeout(r, 150));
            await video.play().catch(() => {});
          }

          await waitForVideoReady(video);
        }

        scheduleNext();
      } catch (err) {
        console.error("No se pudo iniciar cámara:", err);
        if (!cancelledRef.current) {
          showFeedback("❌ No se pudo iniciar la cámara seleccionada", "error");
        }
      }
    };

    startStream();

    return () => {
      cancelledRef.current = true;
      stopCurrentStream();
      scanInProgressRef.current = false;
    };
  }, [
    scanMode,
    selectedDeviceId,
    preferredFacingMode,
    handleResult,
    hasPermission,
    isMobile,
    stopCurrentStream,
    decodeFrame,
    showFeedback,
  ]);

  const handleScanModeChange = (_event, nextMode) => {
    if (!nextMode || nextMode === scanMode) return;
    setScanMode(nextMode);
    setGunBuffer("");
  };

  const handleRegistrar = async () => {
    if (registering) return;

    if (!scannedItems.length) {
      showFeedback("Escanea al menos una muestra", "warning");
      return;
    }
    if (!selectionOnly && !isEnvioDestinoValid(destino)) {
      showFeedback("Completa destino y persona de envío", "warning");
      return;
    }

    const courier = numCourier.trim() || null;
    const fechaEnvio = dayjs().format("YYYY-MM-DD");
    const destinoPayload = toMovimientoDestinoPayload(destino);
    const items = [...scannedItems];
    const total = items.length;
    let okCount = 0;
    const failedIds = new Set();

    setRegistering(true);
    setRegisterProgress({ done: 0, total });

    try {
      if (typeof customRegisterRef.current === "function") {
        let result;
        try {
          result = await customRegisterRef.current({
            items,
            destino,
            numCourier: courier,
            fechaEnvio,
            destinoPayload,
            setProgress: (done) => setRegisterProgress({ done, total }),
          });
        } catch (err) {
          const msg =
            err?.response?.data?.error || err?.message || "Error al registrar";
          showFeedback(`❌ ${msg}`, "error");
          return;
        }
        const customOk = Number(result?.okCount ?? items.length);
        const customFailed = new Set(result?.failedIds || []);
        if (customFailed.size === 0) {
          showFeedback(
            result?.successMessage || `✅ Registrado: ${customOk} muestra(s)`,
            "success",
          );
          clearList();
          setNumCourier("");
          if (typeof onRegisteredRef.current === "function") {
            await onRegisteredRef.current(result);
          }
        } else if (customOk === 0) {
          showFeedback(
            result?.errorMessage || "❌ Error al registrar",
            "error",
          );
        } else {
          setScannedItems((prev) =>
            prev.filter((item) => customFailed.has(item.ID_MUESTRA)),
          );
          showFeedback(
            `⚠️ ${customOk} ok, ${customFailed.size} con error. Revisa la lista.`,
            "warning",
          );
        }
        return;
      }

      for (let i = 0; i < items.length; i += 1) {
        const item = items[i];
        const movimientoData = {
          ID_MUESTRA: item.ID_MUESTRA,
          FECHA_ENVIO: fechaEnvio,
          NUM_COURIER: courier,
          ...destinoPayload,
        };

        try {
          const created = await addMovimiento(movimientoData);
          socket.emit("movimiento-registrado", created || movimientoData);
          okCount += 1;
        } catch {
          failedIds.add(item.ID_MUESTRA);
        }

        setRegisterProgress({ done: i + 1, total });
      }

      if (failedIds.size === 0) {
        showFeedback(`✅ Envío registrado: ${okCount} muestra(s)`, "success");
        clearList();
        setNumCourier("");
        if (typeof onRegisteredRef.current === "function") {
          await onRegisteredRef.current({ okCount });
        }
      } else if (okCount === 0) {
        showFeedback("❌ Error al registrar el envío", "error");
      } else {
        setScannedItems((prev) =>
          prev.filter((item) => failedIds.has(item.ID_MUESTRA)),
        );
        showFeedback(
          `⚠️ ${okCount} ok, ${failedIds.size} con error. Revisa la lista.`,
          "warning",
        );
      }
    } finally {
      setRegistering(false);
      setRegisterProgress({ done: 0, total: 0 });
    }
  };

  const requestRegistrar = () => {
    if (selectionOnly && confirmSelection) {
      setConfirmSelectionOpen(true);
      return;
    }
    handleRegistrar();
  };

  const switchCamera = () => {
    if (devices.length > 1) {
      const currentIndex = devices.findIndex(
        (d) => d.deviceId === selectedDeviceId,
      );
      const nextIndex = (currentIndex + 1) % devices.length;
      setSelectedDeviceId(devices[nextIndex].deviceId);
    }
    setPreferredFacingMode((prev) =>
      prev === "environment" ? "user" : "environment",
    );
  };

  const itemCount = scannedItems.length;

  return (
    <Box
      sx={{
        p: { xs: 1.5, sm: 3 },
        textAlign: "center",
        maxWidth: 420,
        width: "100%",
        margin: "auto",
        boxSizing: "border-box",
        pb: { xs: 4, sm: 3 },
        ...(embedded ? {} : contentMargin),
      }}
    >
      {title ? (
        <Typography variant="h6" sx={{ mb: 1.5 }}>
          {title}
        </Typography>
      ) : null}

      <ToggleButtonGroup
        exclusive
        fullWidth
        size="small"
        value={scanMode}
        onChange={handleScanModeChange}
        disabled={registering}
        sx={{ mb: 2 }}
      >
        <ToggleButton
          value={SCAN_MODE_CAMERA}
          sx={{ textTransform: "none", gap: 0.75 }}
        >
          <PhotoCameraIcon fontSize="small" />
          Cámara
        </ToggleButton>
        <ToggleButton
          value={SCAN_MODE_GUN}
          sx={{ textTransform: "none", gap: 0.75 }}
        >
          <QrCodeScannerIcon fontSize="small" />
          Pistola
        </ToggleButton>
      </ToggleButtonGroup>

      {scanMode === SCAN_MODE_CAMERA ? (
        <Box
          sx={{
            border: "2px solid #90b2b7",
            borderRadius: 2,
            overflow: "hidden",
            mb: 2,
            position: "relative",
            aspectRatio: "1 / 1",
            width: "100%",
            maxWidth: { xs: "100%", sm: 420 },
            mx: "auto",
            background: "#000",
          }}
        >
          {!hasPermission && hasPermission !== null && (
            <Typography
              color="error"
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                textAlign: "center",
                px: 2,
              }}
            >
              ⚠️ No se puede acceder a la cámara. <br /> Revisa los permisos.
            </Typography>
          )}

          {hasPermission && (
            <>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                }}
              />
              <canvas ref={canvasRef} style={{ display: "none" }} />
            </>
          )}

          {hasPermission === null && (
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                color: "white",
                textAlign: "center",
              }}
            >
              <CircularProgress color="inherit" />
              <Typography sx={{ mt: 1 }}>Inicializando cámara...</Typography>
            </Box>
          )}

          {loading && (
            <Box
              sx={{
                position: "absolute",
                top: 10,
                left: 10,
                bgcolor: "rgba(0,0,0,0.55)",
                color: "white",
                px: 1.25,
                py: 0.5,
                borderRadius: 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
                zIndex: 2,
              }}
            >
              <CircularProgress size={14} color="inherit" />
              <Typography variant="caption">Buscando muestra...</Typography>
            </Box>
          )}

          {itemCount > 0 && (
            <Box
              sx={{
                position: "absolute",
                top: 10,
                right: 10,
                bgcolor: "rgba(0,0,0,0.55)",
                color: "white",
                px: 1.25,
                py: 0.5,
                borderRadius: 1,
                zIndex: 2,
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                {itemCount} muestra{itemCount === 1 ? "" : "s"}
              </Typography>
            </Box>
          )}

          {hasMultipleCameras && (
            <Button
              onClick={switchCamera}
              aria-label="Cambiar cámara"
              disabled={registering}
              sx={{
                position: "absolute",
                bottom: 10,
                right: 10,
                borderRadius: "50%",
                minWidth: 0,
                width: 56,
                height: 56,
                background: "linear-gradient(45deg, #6fb1fc, #4364f7)",
                color: "white",
                boxShadow: "0 3px 6px rgba(0,0,0,0.25)",
                zIndex: 2,
                "&:hover": {
                  background: "linear-gradient(45deg, #5a9ff6, #3959e6)",
                },
              }}
            >
              <CameraswitchIcon />
            </Button>
          )}
        </Box>
      ) : (
        <Box
          sx={{
            border: "2px solid #90b2b7",
            borderRadius: 2,
            mb: 2,
            p: 2.5,
            textAlign: "left",
            bgcolor: "background.paper",
          }}
        >
          <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
            Modo pistola / código
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Escanea el QR, o escribe el código de la etiqueta (el texto bajo el
            QR) y pulsa Enter o Añadir.
          </Typography>
          <TextField
            inputRef={gunInputRef}
            label="Escanea o escribe el código"
            value={gunBuffer}
            onChange={(e) => setGunBuffer(e.target.value)}
            onKeyDown={handleGunKeyDown}
            onBlur={() => setTimeout(focusGunInput, 80)}
            fullWidth
            autoFocus
            autoComplete="off"
            disabled={registering || loading}
            placeholder="Código de muestra…"
            helperText={
              loading
                ? "Buscando muestra…"
                : itemCount > 0
                  ? `${itemCount} muestra(s) en la lista`
                  : "Pistola, o código a mano + Enter"
            }
            InputProps={{
              endAdornment: loading ? (
                <CircularProgress size={18} sx={{ mr: 1 }} />
              ) : (
                <Button
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={submitGunScan}
                  disabled={!gunBuffer.trim() || registering}
                  sx={{ textTransform: "none", whiteSpace: "nowrap" }}
                >
                  Añadir
                </Button>
              ),
            }}
          />
        </Box>
      )}

      <TextField
        label="Último código detectado"
        value={lastQrCode}
        fullWidth
        disabled
        size="small"
        sx={{ mb: 2 }}
      />

      {itemCount > 0 && (
        <Box sx={{ textAlign: "left", mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Typography sx={{ fontWeight: 600 }}>
              {selectionOnly ? selectionListTitle : "Envío"} ({itemCount})
            </Typography>
            <Button
              size="small"
              onClick={clearList}
              disabled={registering}
              sx={{ textTransform: "none" }}
            >
              Vaciar lista
            </Button>
          </Box>

          <List
            dense
            sx={{
              maxHeight: 180,
              overflowY: "auto",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              mb: 2,
              bgcolor: "background.paper",
            }}
          >
            {scannedItems.map((item) => (
              <ListItem key={item.ID_MUESTRA} divider>
                <ListItemText
                  primary={item.MUESTRA}
                  secondary={item.PRODUCTO || "Sin producto / diseño"}
                  primaryTypographyProps={{
                    fontWeight: 800,
                    fontSize: 14,
                    color: "#00c2a3",
                  }}
                  secondaryTypographyProps={{
                    fontSize: 12,
                    color: "#E57374",
                    fontWeight: 600,
                  }}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    aria-label="Quitar muestra"
                    onClick={() => removeItem(item.ID_MUESTRA)}
                    disabled={registering}
                    size="small"
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>

          {!selectionOnly ? (
            <Box sx={{ mb: 2, textAlign: "left" }}>
              <EnvioDestinoFields
                value={destino}
                onChange={setDestino}
                disabled={registering}
                allowedTipos={allowedTipos}
              />
            </Box>
          ) : null}

          {!selectionOnly && !hideCourier ? (
            <TextField
              label="Nº Courier"
              value={numCourier}
              onChange={(e) => setNumCourier(e.target.value)}
              fullWidth
              disabled={registering}
              sx={{ mb: 2 }}
            />
          ) : null}

          <Button
            variant="contained"
            fullWidth
            onClick={requestRegistrar}
            disabled={
              registering || (!selectionOnly && !isEnvioDestinoValid(destino))
            }
            sx={{
              background: "#90b2b7",
              fontWeight: 600,
              "&:hover": { background: "#bfe9ef" },
              "&.Mui-disabled": {
                background: "#c5d5d8",
                color: "#fff",
              },
            }}
          >
            {registering
              ? `${selectionOnly ? "Generando" : "Registrando"} ${
                  registerProgress.done
                }/${registerProgress.total}…`
              : confirmLabel
                ? `${confirmLabel} (${itemCount})`
                : `Registrar envío (${itemCount})`}
          </Button>
        </Box>
      )}

      {selectionFooter}

      <Dialog
        open={confirmSelectionOpen}
        onClose={() => !registering && setConfirmSelectionOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Generar presentación PDF</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Se generará una presentación con {itemCount} muestra
            {itemCount === 1 ? "" : "s"}. ¿Quieres continuar?
          </DialogContentText>
          {itemCount > 20 ? (
            <Alert severity="warning" sx={{ mt: 2 }}>
              En móviles, una presentación grande puede tardar o consumir mucha
              memoria.
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmSelectionOpen(false)}
            disabled={registering}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setConfirmSelectionOpen(false);
              handleRegistrar();
            }}
            disabled={registering || itemCount === 0}
            startIcon={<PictureAsPdfOutlinedIcon />}
          >
            Generar PDF
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={open}
        autoHideDuration={3000}
        onClose={() => setOpen(false)}
      >
        <Alert
          severity={alertSeverity}
          onClose={() => setOpen(false)}
          sx={{ width: "100%" }}
        >
          {mensaje}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MuestrasMovimientoCamara;
